import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import moment, { isMoment, Moment } from 'moment';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import i18n from './translate';
import { ClientOption, GetCasesResponse, products, Stage, stages, StateOption } from './types';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { SortDirection } from '@angular/material/sort';
import { WorkflowService } from '../services/workflow/workflow.service';
import { ActivatedRoute } from '@angular/router';
import { UNKNOWN_CLIENT_NAME, UNKNOWN_STATE_LABEL } from './payee-cases.component';
import { z } from 'zod';

@Injectable({
  providedIn: 'root'
})
export class PayeeCasesService {
  private readonly destroy = new Subject<void>();
  readonly setCasesSubject = new Subject<void>();

  readonly getCasesPath = new BehaviorSubject<string | null>(null);
  readonly getDebtorsPath = new BehaviorSubject<string | null>(null);

  // Filters
  readonly client = new BehaviorSubject<ClientOption | null>(null);
  readonly stage = new BehaviorSubject<Stage | null>(null);
  readonly state = new BehaviorSubject<StateOption | null>(null);
  readonly payee_case_reference_id = new BehaviorSubject<string | null>(null);
  readonly debtor_name = new BehaviorSubject<string | null>(null);
  readonly bulk_upload_name = new BehaviorSubject<string | null>(null);
  readonly started_at_min = new BehaviorSubject<string | Moment | null>(null);
  readonly started_at_max = new BehaviorSubject<string | Moment | null>(null);
  readonly product = new BehaviorSubject<string | null>(null);

  // Sorts
  readonly sort_by = new BehaviorSubject<string | null>(null);
  readonly sort_direction = new BehaviorSubject<SortDirection>('');

  // Pagination
  readonly perPage = new BehaviorSubject<number>(10);
  readonly page = new BehaviorSubject<number>(1);

  readonly filters = merge(
    this.client,
    this.stage,
    this.state,
    this.payee_case_reference_id,
    this.debtor_name,
    this.bulk_upload_name,
    this.started_at_min,
    this.started_at_max,
    this.product,
    this.sort_by,
    this.sort_direction,
    this.perPage,
    this.page,
  )
    .pipe<null>(map(() => null));

  readonly cases = new Subject<GetCasesResponse>();
  readonly loading = new BehaviorSubject<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    private translate: TranslateService,
    private workflowService: WorkflowService,
  ) {
    for (const [lang, translation] of Object.entries(i18n)) {
      this.translate.setTranslation(lang, translation, true);
    }
  }

  onInit(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.setCasesSubject.next();
      });

    this.filters
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setCasesSubject.next());

    this.workflowService.afterWorkflow
      .pipe(takeUntil(this.destroy))
      .subscribe(async () => await this.setCases());

    this.route.queryParams
      .pipe(takeUntil(this.destroy))
      .subscribe(params => {
        const stage = params.stage;
        if (stages.includes(stage)) {
          this.stage.next(stage);
        }

        const product = params.product;
        if (products.includes(product)) {
          this.product.next(product);
        }

        const state = params.state;
        if (state) {
          this.state.next({
            id: state,
            label: UNKNOWN_STATE_LABEL,
          });
        }

        const clientId = params.client_id;
        if (z.string().uuid().safeParse(clientId).success) {
          this.client.next({
            id: clientId,
            name: UNKNOWN_CLIENT_NAME,
          });
        }

        this.payee_case_reference_id.next(params.payee_case_reference_id || null);
        this.debtor_name.next(params.debtor_name || null);

        const startedAtMin = params.startedAtMin;
        if (moment(startedAtMin, 'YYYY-MM-DD').isValid()) {
          this.started_at_min.next(startedAtMin);
        }

        const startedAtMax = params.startedAtMax;
        if (moment(startedAtMax, 'YYYY-MM-DD').isValid()) {
          this.started_at_max.next(startedAtMax);
        }

        const sort_by = params.sort_by;
        if (sort_by) {
          this.sort_by.next(sort_by);
        }

        if (sort_by) {
          let sort_direction = params.sort_direction;
          if (!(['asc', 'desc'].includes(sort_direction))) {
            sort_direction = 'asc';
          }
          this.sort_direction.next(sort_direction);
        }

        const page = parseInt(params.page);
        if (!isNaN(page)) {
          this.page.next(Math.max(page, 1));
        }

        const perPage = parseInt(params.perPage);
        if (!isNaN(perPage)) {
          this.perPage.next(Math.max(perPage, 1));
        }
      });

    this.loading.next(true);

    this.setCasesSubject
      .pipe(
        takeUntil(this.destroy),
        startWith(this.getFilters()),
        debounceTime(200),
        map(() => JSON.stringify(this.getFilters())),
        distinctUntilChanged(),
      )
      .subscribe(async () => {
        await this.setCases();
      });
  }

  onDestroy(): void {
    this.destroy.next();
  }

  setPagination(event: PageEvent): PayeeCasesService {
    if (this.perPage.value === event.pageSize) {
      this.page.next(event.pageIndex + 1);
    } else {
      this.page.next(1);
    }
    this.perPage.next(event.pageSize);

    return this;
  }

  resetFilters(): PayeeCasesService {
    this.client.next(null);
    this.stage.next(null);
    this.state.next(null);
    this.payee_case_reference_id.next(null);
    this.debtor_name.next(null);
    this.bulk_upload_name.next(null);
    this.started_at_min.next(null);
    this.started_at_max.next(null);
    this.product.next(null);
    this.perPage.next(10);
    this.page.next(1);

    return this;
  }

  getFilters(): Record<string, string | number | null> {
    let startedAtMin = this.started_at_min.value;
    if (isMoment(startedAtMin)) {
      startedAtMin = startedAtMin.format('YYYY-MM-DD');
    }

    let startedAtMax = this.started_at_max.value;
    if (isMoment(startedAtMax)) {
      startedAtMax = startedAtMax.format('YYYY-MM-DD');
    }

    return {
      stage: this.stage.value,
      state: this.state.value?.id ?? null,
      payee_case_reference_id: this.payee_case_reference_id.value,
      debtor_name: this.debtor_name.value,
      bulk_upload_name: this.bulk_upload_name.value || null,
      startedAtMin,
      startedAtMax,
      product: this.product.value,
      page: this.page.value,
      sort_by: this.sort_direction.value ? this.sort_by.value : null,
      sort_direction: this.sort_direction.value || null,
      perPage: this.perPage.value,
      client_id: this.client.value?.id ?? null,
    };
  }

  async setCases(): Promise<void> {
    if (!this.getCasesPath.value) {
      console.warn('Get cases path is not set!');
      return;
    }

    try {
      this.loading.next(true);

      const result = await axios.get<GetCasesResponse>(
        `${environment.baseUrl}/${this.getCasesPath.value}`,
        {
          params: this.getFilters(),
        }
      );

      this.cases.next(result.data);

      if (
        result.data.data.length > 0
        && this.stage.value
        && result.data.data.every(v => v.state.stage !== this.stage.value)
      ) {
        this.stage.next(result.data.data[0].state.stage);
      }
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while getting cases table', error.response?.data);
      } else {
        console.error('Unknown error while getting cases table', error);
      }

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.ERROR_MESSAGES.ERROR_WHILE_LOADING_CASES'),
        'OK',
        {
          duration: 5000,
        }
      );
    } finally {
      this.loading.next(false);
    }
  }
}
